import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    Clock,
    Copy,
    Mail,
    Megaphone,
    MapPin,
    Phone,
    Tag,
    Trash2,
    User,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { getErrorMessage } from '@/utils/errorHandling';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import {
    useConvertLeadToCustomer,
    useDeleteLead,
    useLead,
    useLeadNavigation,
    useUpdateLead,
} from '@/api/leads/use-leads.tsx';
import { Lead } from '@/models/lead.ts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineEdit } from '@/components/ui/inline-edit.tsx';
import { Click2CallTrigger } from '@/components/ui/click-2-call-trigger';
import { SendMailTrigger } from '@/components/ui/send-mail-trigger';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import DuplicateBadge from '@/components/leads/DuplicateBadge';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog.tsx';
import { NotesWidget } from '@/components/notes/NotesWidget';
import { useActiveLeadStatuses } from '@/api/lead-statuses/use-lead-statuses';
import { useUsers } from '@/api/users/use-users';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes';
import DynamicInput from '@/components/ui/DynamicInput';
import { customAttributesToRequestShape } from '@/utils/customAttributes';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';

const LeadDetailsSkeleton: React.FC = () => (
    <PageContainer>
        {/* Header */}
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
                <Skeleton className="h-9 w-32 rounded-md" />
                <div className="space-y-1">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-4 w-24" />
                </div>
            </div>
            <div className="flex gap-2">
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-40 rounded-md" />
                <Skeleton className="h-9 w-20 rounded-md" />
            </div>
        </div>

        {/* Tabs bar */}
        <div className="flex gap-2">
            <Skeleton className="h-10 w-24 rounded-md" />
            <Skeleton className="h-10 w-36 rounded-md" />
        </div>

        {/* Tab content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-40" />
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton
                                        className="h-6"
                                        style={{ width: `${55 + ((i * 13) % 35)}%` }}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-44" />
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <Skeleton className="h-4 w-28" />
                                    <Skeleton className="h-5 w-36" />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-44" />
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="space-y-2">
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-8 w-full rounded-md" />
                            </div>
                        ))}
                    </CardContent>
                </Card>
            </div>
        </div>
    </PageContainer>
);

const LeadDetailsPage: React.FC = () => {
    const { t } = useTranslation('crm');
    const { leadId } = useParams<{ leadId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();

    const { hasPermission } = usePermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [phonePrimaryEditing, setPhonePrimaryEditing] = useState(false);
    const [phoneAdditionalEditing, setPhoneAdditionalEditing] = useState(false);

    const filters: Record<string, string> = {};
    const sortConfig: { key: string; direction: 'asc' | 'desc' } = {
        key: searchParams.get('sort') || 'created_at',
        direction: (searchParams.get('direction') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
    };

    searchParams.forEach((value, key) => {
        if (['sort', 'direction', 'page', 'pageSize'].includes(key)) return;
        if (key.startsWith('filter_')) {
            filters[key.replace('filter_', '')] = value;
        } else {
            filters[key] = value;
        }
    });

    const { data: lead, isLoading, refetch } = useLead(leadId || '');
    const { data: navigation } = useLeadNavigation(leadId || '', filters, sortConfig);
    const updateLeadMutation = useUpdateLead();
    const deleteLeadMutation = useDeleteLead();
    const convertLeadMutation = useConvertLeadToCustomer();

    const { data: leadStatusesData } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesData?.data || [];

    const { definitions: customAttributeDefinitions, isLoading: isLoadingCustomAttributes } =
        useCustomAttributeDefinitionsManagement();

    const { data: usersResponse } = useUsers({ status: 'active', per_page: 100 });
    const users = usersResponse?.data || [];

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' }))
            .catch(() => enqueueSnackbar(`Failed to copy ${label}`, { variant: 'error' }));
    };

    const handleFieldUpdate = async (field: keyof Lead, value: any) => {
        try {
            await updateLeadMutation.mutateAsync({ id: leadId || '', data: { [field]: value } });
            enqueueSnackbar('Lead updated successfully', { variant: 'success' });
            refetch();
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, 'Failed to update lead'), { variant: 'error' });
        }
    };

    const handleCustomAttributeUpdate = async (attributeName: string, value: any) => {
        try {
            const current = customAttributesToRequestShape(lead?.custom_attributes ?? {});
            await updateLeadMutation.mutateAsync({
                id: leadId || '',
                data: { custom_attributes: { ...current, [attributeName]: value } },
            });
            enqueueSnackbar('Custom attribute updated successfully', { variant: 'success' });
            refetch();
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, 'Failed to update custom attribute'), {
                variant: 'error',
            });
        }
    };

    const handleBackClick = () => {
        const queryString = searchParams.toString();
        navigate(queryString ? `/leads?${queryString}` : '/leads');
    };

    const handlePreviousLead = () => {
        if (navigation?.previous) {
            const queryString = searchParams.toString();
            navigate(
                queryString
                    ? `/leads/${navigation.previous.id}?${queryString}`
                    : `/leads/${navigation.previous.id}`
            );
        }
    };

    const handleNextLead = () => {
        if (navigation?.next) {
            const queryString = searchParams.toString();
            navigate(
                queryString
                    ? `/leads/${navigation.next.id}?${queryString}`
                    : `/leads/${navigation.next.id}`
            );
        }
    };

    const handleDeleteConfirm = () => {
        if (lead) {
            deleteLeadMutation.mutate(lead.id, {
                onSuccess: () => {
                    enqueueSnackbar('Lead deleted successfully', { variant: 'success' });
                    handleBackClick();
                },
                onError: () => enqueueSnackbar('Failed to delete lead', { variant: 'error' }),
            });
        }
    };

    const handleConvertToCustomer = () => {
        if (lead) {
            convertLeadMutation.mutate(lead.id, {
                onSuccess: data => {
                    enqueueSnackbar('Lead successfully converted to customer', {
                        variant: 'success',
                    });
                    navigate(`/customers/${data.customer_id}`);
                },
                onError: error => {
                    enqueueSnackbar(error.message || 'Failed to convert lead to customer', {
                        variant: 'error',
                    });
                },
            });
        }
    };

    if (isLoading) {
        return <LeadDetailsSkeleton />;
    }

    if (!lead) {
        return (
            <PageContainer>
                <PageHeader title="Lead not found" backTo="/leads" backLabel="Back to leads" />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title={lead.name}
                description="Lead Details"
                breadcrumbs={[{ label: 'Leads', to: '/leads' }, { label: lead.name }]}
                backTo="/leads"
                backLabel="Back to leads"
                meta={
                    <div className="flex items-center gap-2">
                        <DuplicateBadge recordId={lead.id} />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePreviousLead}
                            disabled={!navigation?.previous}
                            aria-label="Previous lead"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextLead}
                            disabled={!navigation?.next}
                            aria-label="Next lead"
                        >
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                }
                actions={
                    <>
                        {!lead.converted_at &&
                            hasPermission(ModulePermission.CUSTOMERS, PermissionAction.CREATE) && (
                                <Button
                                    variant="success"
                                    onClick={handleConvertToCustomer}
                                    disabled={convertLeadMutation.isPending}
                                >
                                    <User className="h-4 w-4" />
                                    {convertLeadMutation.isPending
                                        ? 'Converting...'
                                        : 'Convert to Customer'}
                                </Button>
                            )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.DELETE) && (
                            <Button
                                variant="destructive"
                                onClick={() => setIsDeleteDialogOpen(true)}
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete
                            </Button>
                        )}
                    </>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Lead Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Name
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <InlineEdit
                                            value={lead.name}
                                            onSave={value => handleFieldUpdate('name', value)}
                                            permissionModule={ModulePermission.LEADS}
                                            className="text-lg font-semibold"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={() => copyToClipboard(lead.name, 'Name')}
                                            aria-label="Copy name to clipboard"
                                            title="Copy name to clipboard"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Email
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={lead.email}
                                            onSave={value => handleFieldUpdate('email', value)}
                                            permissionModule={ModulePermission.LEADS}
                                            className="flex-1 min-w-0"
                                        />
                                        <SendMailTrigger
                                            email={lead.email}
                                            recipientId={lead.id}
                                            recipientType="lead"
                                            recipientName={lead.name}
                                            className="flex-shrink-0"
                                        />
                                        {lead.email && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 flex-shrink-0"
                                                onClick={() => copyToClipboard(lead.email, 'Email')}
                                                aria-label="Copy email to clipboard"
                                                title="Copy email to clipboard"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Phone
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={lead.phone}
                                            onSave={value => handleFieldUpdate('phone', value)}
                                            permissionModule={ModulePermission.LEADS}
                                            placeholder="No phone number"
                                            type="phone"
                                            className="flex-1 min-w-0"
                                            onEditingChange={setPhonePrimaryEditing}
                                        />
                                        {!phonePrimaryEditing && (
                                            <>
                                                <Click2CallTrigger
                                                    value={lead.phone || ''}
                                                    displayMode="buttonOnly"
                                                    className="flex-shrink-0"
                                                />
                                                {lead.phone && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 flex-shrink-0"
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                lead.phone || '',
                                                                'Phone'
                                                            )
                                                        }
                                                        aria-label="Copy phone to clipboard"
                                                        title="Copy phone to clipboard"
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Additional Phone Number
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={lead.additional_phone_number}
                                            onSave={value =>
                                                handleFieldUpdate('additional_phone_number', value)
                                            }
                                            permissionModule={ModulePermission.LEADS}
                                            placeholder="No additional phone number"
                                            type="phone"
                                            className="flex-1 min-w-0"
                                            onEditingChange={setPhoneAdditionalEditing}
                                        />
                                        {!phoneAdditionalEditing && (
                                            <>
                                                <Click2CallTrigger
                                                    value={lead.additional_phone_number || ''}
                                                    displayMode="buttonOnly"
                                                    className="flex-shrink-0"
                                                />
                                                {lead.additional_phone_number && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 flex-shrink-0"
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                lead.additional_phone_number || '',
                                                                'Additional Phone'
                                                            )
                                                        }
                                                        aria-label="Copy additional phone to clipboard"
                                                        title="Copy additional phone to clipboard"
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Country
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        <InlineEdit
                                            value={lead.country || ''}
                                            onSave={value => handleFieldUpdate('country', value)}
                                            permissionModule={ModulePermission.LEADS}
                                            placeholder="No country"
                                            type="country"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Assigned To
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        <InlineEdit
                                            value={lead.assigned_user?.name || ''}
                                            onSave={value => {
                                                if (!value || value === '__unassigned__') {
                                                    handleFieldUpdate('assigned_user_id', null);
                                                } else {
                                                    const selectedUser = users.find(
                                                        u => u.name === value
                                                    );
                                                    handleFieldUpdate(
                                                        'assigned_user_id',
                                                        selectedUser?.id || null
                                                    );
                                                }
                                            }}
                                            permissionModule={ModulePermission.LEADS}
                                            placeholder="No assignee"
                                            type="select"
                                            selectOptions={[
                                                {
                                                    value: '__unassigned__',
                                                    label: 'Unassigned',
                                                },
                                                ...users.map(u => ({
                                                    value: u.name,
                                                    label: `${u.name}${u.title ? ` (${u.title})` : ''}`,
                                                })),
                                            ]}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Status
                                    </label>
                                    <div className="mt-1">
                                        <InlineEdit
                                            value={lead.status?.label || ''}
                                            onSave={value =>
                                                handleFieldUpdate('lead_status', value)
                                            }
                                            permissionModule={ModulePermission.LEADS}
                                            type="select"
                                            selectOptions={leadStatuses.map(s => ({
                                                value: s.id,
                                                label: s.label,
                                            }))}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Preferred Language
                                    </label>
                                    <div className="flex items-center gap-2 mt-1">
                                        <InlineEdit
                                            value={
                                                lead.preferred_language ||
                                                lead.settings?.preferred_language ||
                                                'en'
                                            }
                                            onSave={value =>
                                                handleFieldUpdate('preferred_language', value)
                                            }
                                            permissionModule={ModulePermission.LEADS}
                                            placeholder="No language set"
                                            type="select"
                                            selectOptions={[
                                                { value: 'en', label: 'English' },
                                                {
                                                    value: 'ar',
                                                    label: 'العربية (Arabic)',
                                                },
                                                {
                                                    value: 'es',
                                                    label: 'Español (Spanish)',
                                                },
                                                {
                                                    value: 'fr',
                                                    label: 'Français (French)',
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        {t('campaigns.leadField.label')}
                                    </label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Megaphone className="h-4 w-4 text-muted-foreground" />
                                        {lead.campaign_id ? (
                                            <Link
                                                to={`/campaigns/${lead.campaign_id}`}
                                                className="text-sm font-medium text-primary hover:underline"
                                            >
                                                {lead.campaign?.name ?? lead.campaign_id}
                                            </Link>
                                        ) : (
                                            <span className="text-sm text-muted-foreground">
                                                {t('campaigns.leadField.none')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="h-5 w-5" />
                                Contact & Follow-up
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Contact Attempts
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {lead.contact_attempts}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Last Contact
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {lead.last_contact_date ? (
                                                <DateTimeFormatter
                                                    date={lead.last_contact_date}
                                                    timeZone={crmTimezone}
                                                />
                                            ) : (
                                                'Never'
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Next Follow-up
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {lead.next_follow_up_date ? (
                                                <DateTimeFormatter
                                                    date={lead.next_follow_up_date}
                                                    timeZone={crmTimezone}
                                                />
                                            ) : (
                                                'Not set'
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Created
                                    </label>
                                    <DateTimeFormatter
                                        date={lead.created_at}
                                        timeZone={crmTimezone}
                                        showIcon={true}
                                        icon={
                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                        }
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Tag className="h-5 w-5" />
                                Additional Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoadingCustomAttributes ? (
                                <div className="space-y-4">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} className="space-y-2">
                                            <Skeleton className="h-4 w-28" />
                                            <Skeleton className="h-8 w-full rounded-md" />
                                        </div>
                                    ))}
                                </div>
                            ) : customAttributeDefinitions &&
                              customAttributeDefinitions.length > 0 ? (
                                customAttributeDefinitions.map(definition => {
                                    const customValue =
                                        lead?.custom_attributes?.[definition.name as any]?.value ||
                                        '';
                                    return (
                                        <div key={definition.id}>
                                            <label className="text-sm font-medium text-muted-foreground">
                                                {definition.label}
                                                {definition.is_required && (
                                                    <span className="text-destructive ms-1">*</span>
                                                )}
                                            </label>
                                            <div className="mt-1">
                                                <DynamicInput
                                                    definition={definition}
                                                    value={customValue}
                                                    onChange={value =>
                                                        handleCustomAttributeUpdate(
                                                            definition.name,
                                                            value
                                                        )
                                                    }
                                                    mode="inline"
                                                    placeholder={`Enter ${definition.label.toLowerCase()}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    No custom attributes defined.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <NotesWidget entityType="leads" entityId={leadId!} entityName={lead.name} />

            {/* Delete Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Lead</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteConfirm}
                            disabled={deleteLeadMutation.isPending}
                        >
                            {deleteLeadMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default LeadDetailsPage;
